/**
 * Which backgrounds text sits on, for contrast checks. Pure (no DOM): the neutral surface is a
 * parameter because it depends on the theme. Gradients are sampled at both stops and the
 * midpoint; the WORST sample decides, so one weak spot is not hidden by an average.
 */
import { mixHex } from './css';
import type { Look } from './types';

/** Surface colors of the two themes (see `app/styles/tokens.css`). */
export const NEUTRAL_SURFACE = { light: '#ffffff', dark: '#1d2129' } as const;
export const NEUTRAL_COLUMN = { light: '#e9ecf2', dark: '#191d24' } as const;

const HEX = /^#[0-9a-f]{6}$/i;
const hex = (value: string | null | undefined): value is string => !!value && HEX.test(value);

/** Mix `fraction` (0..1) of `color` into `base`. */
function tint(color: string, base: string, fraction: number): string {
  const channel = (offset: number) =>
    Math.round(
      parseInt(color.slice(offset, offset + 2), 16) * fraction +
        parseInt(base.slice(offset, offset + 2), 16) * (1 - fraction),
    )
      .toString(16)
      .padStart(2, '0');
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

/** Colors the text of a CARD with this look can end up on. */
export function cardBackdrops(look: Look | null | undefined, neutral: string): string[] {
  if (!look) return [neutral];
  if (hex(look.surface)) return [look.surface];
  const body = look.advanced?.body;
  if (!look.one_color && hex(body) && look.style !== 'gradient' && look.style !== 'pattern') {
    return [body];
  }
  switch (look.style) {
    case 'solid':
      return [hex(look.color) ? look.color : neutral];
    case 'soft':
      return [hex(look.color) ? tint(look.color, neutral, 0.16) : neutral];
    case 'gradient':
      return look.gradient && hex(look.gradient.start) && hex(look.gradient.end)
        ? [look.gradient.start, mixHex(look.gradient.start, look.gradient.end), look.gradient.end]
        : [neutral];
    case 'pattern':
      return [hex(look.pattern?.background) ? look.pattern.background : neutral];
    default:
      return [neutral];
  }
}

/** Colors the title / body text of a COLUMN (drawn straight on it) can end up on. */
export function columnBackdrops(look: Look | null | undefined, neutralColumn: string): string[] {
  if (!look || look.style === 'standard') return [neutralColumn];
  const body = !look.one_color ? look.advanced?.body : null;
  if (hex(body) && look.style !== 'gradient' && look.style !== 'pattern') return [body];
  switch (look.style) {
    case 'solid':
      return [hex(look.color) ? look.color : neutralColumn];
    case 'soft':
      return [hex(look.color) ? tint(look.color, neutralColumn, 0.12) : neutralColumn];
    case 'gradient':
      return look.gradient && hex(look.gradient.start) && hex(look.gradient.end)
        ? [look.gradient.start, mixHex(look.gradient.start, look.gradient.end), look.gradient.end]
        : [neutralColumn];
    case 'pattern':
      return [hex(look.pattern?.background) ? look.pattern.background : neutralColumn];
    default:
      return [neutralColumn];
  }
}
